import * as React from "react";
import { connect } from "react-redux";

import { IApplicationState } from "src/~store/models/ApplicationState";
import { ITopicEditorProps } from "./~types/TopicEditorProps";

import {
    getAllTopics,
    getTopicByExternalId,
    getTopicAllByExternalId,
    exportPairs,
    publishQuestions,
    importPairs,
    getTopicPairCategoriesByTopicName,
    setFilterTags,
    resetSelectedTopic,
    saveSynonyms,
    getSynonyms,
    undoTopicPairChanges,
    changeTopicPairsVerion,
    setCurrentTopicPairPage,
} from "../~store/actions/actions";
import {
    isTransferQuestionPopupVisibleSelector,
    isOperationInProgressSelector,
    isExportingPairsSelector,
    isPublishingQuestionsSelector,
    isMessageBoxVisibleSelector,
    selectedTopicSelector,
    topicPairCategoriesSelector,
    filterTagsSelector,
    selectedTopicPairStatusSelector,
    topicHasUnpublishedChangesSelector,
    currentTopicPairPageSelector,
    isDelitingTopicPairSelector,
    isSavingTopicPairSelector,
} from "../~store/selectors";

import Loader from "src/app/shared/components/loader/Loader";
import MessageBox from "src/app/shared/components/message-boxes/message-box/MessageBox";
import TopicWidget from "./topic-widget/TopicWidget";
import TopicPairsTabs from "./topic-pairs-tabs/TopicPairsTabs";
import TopicEditorHeader from "./topic-editor-header/TopicEditorHeader";
import TransferQuestionPopup from "./transfer-question-popup/TransferQuestionPopup";
import { buildFileSelector } from "src/app/shared/helpers/file-helper/FileHelper";
import { ISearchingTag } from "src/app/shared/components/tag-searching-input/~types/models/SearchingTag";
import "./TopicEditor.css";
import SynonymsDialog from "./synonyms-dialog/SynonymsDialog";
import useSaveScroll from "src/app/shared/helpers/custom-hooks/useSaveScroll/useSaveScroll";
import { useTranslation } from "react-i18next";
import { NLPDesigner } from "src/app/nlpDesigner";
import { useNavigate, useParams } from "react-router-dom";
import { getTriggerNameTags } from "../~store/actions/constants";

type ReduxType = ReturnType<typeof mapStateToProps> & ITopicEditorProps;

const TopicEditor = (props: ReduxType) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { topicExternalId } = useParams();
    const [isSynonymsDialogVisible, setSynonymsDialogVisible] =
        React.useState<boolean>(false);
    const [isDesignerView, setIsDesignerView] = React.useState(false);

    const { elementRef, resetScroll } = useSaveScroll({
        trigger: !props.isOperationInProgress,
    });

    React.useEffect(() => {
        resetScroll();
    }, [props.selectedTopicPairStatus, props.currentTopicPairPage]);

    React.useEffect(() => {
        if(!props.isSavingTopicPair && !props.isDelitingTopicPair && !props.isPublishingQuestions && props.selectedTopic.name)
        {
            props.getTopicPairCategoriesByTopicName(props.selectedTopic.name);
        }
    }, [props.isSavingTopicPair, props.isDelitingTopicPair, props.isPublishingQuestions])

    React.useEffect(() => {
        return () => {
            props.setFilterTags([]);
            props.resetSelectedTopic();
            props.setCurrentTopicPairPage(1);
        };
    }, []);

    React.useEffect(() => {
        if (!topicExternalId) {
            console.error("There is no topicExternalId provided");
            return;
        }
        props.getTopicByExternalId(topicExternalId, props.currentTopicPairPage, () => navigate("/nlu"));
    }, [props.filterTags, props.selectedTopicPairStatus, props.currentTopicPairPage]);

    React.useEffect(() => {
        if (props.selectedTopic.name.length > 0)
            props.getTopicPairCategoriesByTopicName(props.selectedTopic.name);
    }, [props.selectedTopic.name]);


    const _onPublishQuestionsHandler = (): void => {
        const parameters = JSON.parse(props.selectedTopic.parametersJson) ?? [];
        props.publishQuestions(
            props.selectedTopic.topicExternalId,
            parameters["SearchAlgoType"] ?? ""
        );
    };

    const _onExportPairsHandler = (): void => {
        props.exportPairs(props.selectedTopic.topicExternalId);
    };

    const _onImportPairsHandler = (): void => {
        const onFileToImportSelected = (e: any): void => {
            if (e.target.files.length === 0) return;
            const file = e.target.files[0] as File;
            props.importPairs(props.selectedTopic.topicExternalId, file);
        };
        buildFileSelector(
            ".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel",
            onFileToImportSelected
        ).click();
    };

    const _onSwitchView = (): void => {
        if (!topicExternalId) {
            console.error("There is no topicExternalId provided");
            return;
        }
        if (!isDesignerView) {
            props.getTopicAllByExternalId(topicExternalId);
        } else {
            props.getTopicByExternalId(topicExternalId, props.currentTopicPairPage, () => navigate("/nlu"));
        }
        setIsDesignerView((prevState: boolean) => !prevState);
    };

    const setTriggerNameFilter = (triggerName: string) => {
        _onSwitchView();
        props.selectFilterTags?.(getTriggerNameTags(triggerName));
    };

    const _getTopicEditorControl = (): React.ReactElement<{}> => {
        const {
            selectedTopic,
            isMessageBoxVisible,
            isPublishingQuestions,
            isExportingPairs,
            isTransferQuestionPopupVisible,
            currentTopicPairPage,
            setCurrentTopicPairPage
        } = props;
        if (!selectedTopic || !selectedTopic.topicExternalId) {
            return <div>{t("nlp.messageDontHaveAccessToTopic")}</div>;
        }

        return (
            <React.Fragment>
                <div className="topic-editor-container main-section">
                    <TopicEditorHeader
                        topicId={selectedTopic.topicId}
                        topicName={selectedTopic.name}
                        onExportPairs={_onExportPairsHandler}
                        onPublishQuestions={_onPublishQuestionsHandler}
                        onImportPairs={_onImportPairsHandler}
                        onSynonymsHandler={() => setSynonymsDialogVisible(true)}
                        isPublishingQuestions={isPublishingQuestions}
                        isExportingPairs={isExportingPairs}
                        topicPairCategories={props.topicPairCategories}
                        filterTags={props.filterTags}
                        topicHasUnpublishedChanges={
                            props.topicHasUnpublishedChanges
                        }
                        publishHistory={props.selectedTopic.publishHistoryList.sort(
                            (a, b) => a.version - b.version
                        )}
                        onUndoTopicPairChanges={() =>
                            props.undoTopicPairChanges(
                                selectedTopic.topicExternalId
                            )
                        }
                        onChangeTopicPairsVerion={(publishHistoryId) =>
                            props.changeTopicPairsVerion(
                                selectedTopic.topicExternalId,
                                publishHistoryId
                            )
                        }
                        onSwitchDesignerView={_onSwitchView}
                        isDesignerView={isDesignerView}
                    />
                    {isDesignerView ? (
                        <NLPDesigner
                            topicPairs={props?.selectedTopic?.topicPairs ?? []}
                            onSetTriggerNameFilter={setTriggerNameFilter}
                        />
                    ) : (
                        <TopicPairsTabs
                            topicPairCategories={props.topicPairCategories}
                            topicExternalId={selectedTopic.topicExternalId}
                            topicPairsCount={selectedTopic.topicPairsCount}
                            selectedPage={currentTopicPairPage}
                            setSelectedPage={setCurrentTopicPairPage}
                            topicPairsRef={elementRef}
                        />
                    )}
                    <TopicWidget />
                    {isTransferQuestionPopupVisible && (
                        <TransferQuestionPopup />
                    )}
                    {isMessageBoxVisible && <MessageBox />}
                    {isSynonymsDialogVisible && (
                        <SynonymsDialog
                            onClose={() => setSynonymsDialogVisible(false)}
                            saveSynonyms={(synonymstext, onSuccess) =>
                                props.saveSynonyms(
                                    props.selectedTopic.topicExternalId,
                                    synonymstext,
                                    onSuccess
                                )
                            }
                            getSynonyms={(onSuccess) =>
                                props.getSynonyms(
                                    props.selectedTopic.topicExternalId,
                                    onSuccess
                                )
                            }
                        />
                    )}
                </div>
            </React.Fragment>
        );
    };

    return props.isOperationInProgress ? <Loader /> : _getTopicEditorControl();
};

const mapStateToProps = (
    state: IApplicationState,
    props: ITopicEditorProps
) => {
    return {
        selectedTopic: selectedTopicSelector(state),
        isExportingPairs: isExportingPairsSelector(state),
        isPublishingQuestions: isPublishingQuestionsSelector(state),
        isOperationInProgress: isOperationInProgressSelector(state),
        isTransferQuestionPopupVisible:
            isTransferQuestionPopupVisibleSelector(state),
        isMessageBoxVisible: isMessageBoxVisibleSelector(state),
        topicPairCategories: topicPairCategoriesSelector(state),
        filterTags: filterTagsSelector(state),
        selectedTopicPairStatus: selectedTopicPairStatusSelector(state),
        topicHasUnpublishedChanges: topicHasUnpublishedChangesSelector(state),
        currentTopicPairPage: currentTopicPairPageSelector(state),
        isDelitingTopicPair: isDelitingTopicPairSelector(state),
        isSavingTopicPair: isSavingTopicPairSelector(state),
    };
};

const mapDispatchToProps = (dispatch: any): ITopicEditorProps => {
    return {
        getAllTopics: () => dispatch(getAllTopics()),
        getTopicByExternalId: (
            topicExternalId: string,
            selectedPage: number = 1,
            onGoBackToNLUs?: () => void
        ) => dispatch(getTopicByExternalId(topicExternalId, selectedPage, onGoBackToNLUs)),
        getTopicAllByExternalId: (topicExternalId: string) =>
            dispatch(getTopicAllByExternalId(topicExternalId)),
        exportPairs: (topicExternalId: string) =>
            dispatch(exportPairs(topicExternalId)),
        publishQuestions: (topicExternalId: string, searchAlgoType: string) =>
            dispatch(publishQuestions(topicExternalId, searchAlgoType)),
        undoTopicPairChanges: (topicExternalId: string) =>
            dispatch(undoTopicPairChanges(topicExternalId)),
        changeTopicPairsVerion: (
            topicExternalId: string,
            publishHistoryId: number
        ) =>
            dispatch(changeTopicPairsVerion(topicExternalId, publishHistoryId)),
        importPairs: (topicExternalId: string, file: File) =>
            dispatch(importPairs(topicExternalId, file)),
        setFilterTags: (tags: ISearchingTag[]) => dispatch(setFilterTags(tags)),
        resetSelectedTopic: () => dispatch(resetSelectedTopic()),
        getTopicPairCategoriesByTopicName: (topicName: string) =>
            dispatch(getTopicPairCategoriesByTopicName(topicName)),
        saveSynonyms: (
            topicExternalId: string,
            synonymsText: string,
            onSuccess: () => void
        ) => saveSynonyms(topicExternalId, synonymsText, onSuccess),
        getSynonyms: (
            topicExternalId: string,
            onSuccess: (synonymsText: string) => void
        ) => getSynonyms(topicExternalId, onSuccess),
        selectFilterTags: (tags: ISearchingTag[]) =>
            dispatch(setFilterTags(tags)),
        setCurrentTopicPairPage: (page: number) =>
            dispatch(setCurrentTopicPairPage(page))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TopicEditor);
